/**
 * useStudyAbroadAutoMeta — Path-aware Title + Meta-Description engine for
 * EVERY route nested under `/study-abroad/*`.
 *
 * Purpose
 * -------
 * Eliminates the long-running bug where the recruitment-site fallback
 * ("Best Overseas Recruitment Agency in Delhi | MEA Approved | Zafco HR")
 * was leaking into Study Abroad sub-routes — causing GSC duplicate-title
 * penalties across the entire silo.
 *
 * How it works (post-render normalisation)
 * ----------------------------------------
 * Mounted ONCE at the `StudyAbroadLayout` level. On every route change,
 * AFTER all child page effects have run (including any `usePageMeta(...)`
 * calls), this hook:
 *
 *   1. Inspects the current `document.title`.
 *      - If it matches the recruitment-site leak signature (or is empty),
 *        replaces it with a curated or auto-built Study-Abroad title.
 *      - Otherwise: preserves the child's curated core title, but strips
 *        any trailing `| Zafco …` brand (HR / HRM / Study Abroad / plain
 *        "Zafco") so we can re-append the silo-correct suffix in ONE
 *        canonical form: `| Zafco Study Abroad`.
 *
 *   2. Same logic for `<meta name="description">` — only replaces the
 *      recruitment-leak default, never overwrites a curated child
 *      description.
 *
 *   3. Mirrors title + description to `og:title`, `og:description`,
 *      `twitter:title`, `twitter:description` so social-share previews
 *      stay brand-consistent.
 *
 * Critical: only fires when `location.pathname.startsWith('/study-abroad')`.
 * Recruitment-site routes are entirely untouched.
 *
 * Future-proof
 * ------------
 * No hardcoded route registry. The slug parser handles ANY new route
 * under the silo (including dynamic SSR-style country/subpage combos).
 * A small `CURATED_TITLES` / `CURATED_DESCRIPTIONS` map lets product
 * override hand-tuned copy for high-value landing pages without code
 * branches.
 */


import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SA_SUFFIX = '| Zafco Study Abroad';
const SA_DEFAULT_DESC =
  'MEA-approved Zafco Study Abroad consultants help Indian students with university admissions, student visas, scholarships and end-to-end migration support for the UK, Canada, Australia, Germany, Ireland and Dubai.';

// Recruitment-site leak signatures — if the live title / description matches
// any of these, we treat it as "unset / leaked" and replace fully.
const RECRUITMENT_LEAK_TITLES = [
  'Best Overseas Recruitment Agency in Delhi | MEA Approved | Zafco HR',
];
const RECRUITMENT_LEAK_DESC_PREFIX = 'MEA-approved overseas recruitment & immigration agency in Delhi';

// Brand-suffix regex — any trailing `| Zafco …` block (HR, HRM, Study Abroad, etc.)
// captured so we can strip & re-append the silo-correct suffix.
const TRAILING_BRAND_RE = /\s*[|\u2014\u2013–-]\s*Zafco(?:\s+[A-Za-z& ]+)?\s*$/i;

/* ────────────────────────────────────────────────────────────────────────
 *  CURATED TITLES — hand-tuned copy for the highest-value landing pages.
 *  Format keys without trailing slash, matched with normalized path.
 * ──────────────────────────────────────────────────────────────────────── */
const CURATED_TITLES = {
  '/study-abroad': 'Study Abroad Consultants & Overseas Education',
  '/study-abroad/germany': 'Study in Germany — Top Universities & Admissions',
  '/study-abroad/uk': 'Study in UK — Top Universities & Admissions',
  '/study-abroad/canada': 'Study in Canada — Top Universities & Admissions',
  '/study-abroad/australia': 'Study in Australia — Top Universities & Admissions',
  '/study-abroad/ireland': 'Study in Ireland — Top Universities & Admissions',
  '/study-abroad/dubai': 'Study in Dubai — Top Universities & Admissions',
  '/study-abroad/cost-calculator': 'International Student Cost Calculator',
  '/study-abroad/visa-eligibility': 'Student Visa Eligibility Checker',
  '/study-abroad/visa-eligibility-checker': 'Student Visa Eligibility Checker',
  '/study-abroad/compare': 'Compare Study-Abroad Destinations',
  '/study-abroad/book-counselling': 'Book Free Counselling',
  '/study-abroad/free-profile-evaluation': 'Free Profile Evaluation',
  '/study-abroad/about-zafco': 'About Zafco Study Abroad',
  '/study-abroad/why-choose-zafco': 'Why Choose Zafco Study Abroad',
  '/study-abroad/global-partnerships': 'Global University Partnerships',
  '/study-abroad/privacy-policy': 'Privacy Policy',
  '/study-abroad/visa-comparison': 'Student Visa Comparison',
  // ─── Germany Resource Cluster (Feb 2026 build) ─────────────────────
  '/study-abroad/germany/aps-certificate':     'APS Certificate Germany 2026 — Requirements & Process',
  '/study-abroad/germany/blocked-account':     'Germany Blocked Account 2026 — €11,904 Funding Guide',
  '/study-abroad/germany/cost-of-study':       'Cost of Studying in Germany 2026 — Full Budget Breakdown',
  '/study-abroad/germany/scholarships':        'Germany Scholarships 2026 — DAAD, Erasmus+ & Deutschlandstipendium',
  '/study-abroad/germany/public-universities': 'Germany Public Universities 2026 — TU9 & Tuition-Free Guide',
  '/study-abroad/germany/ausbildung':           'Ausbildung in Germany 2026 — Paid 3-Year Trade Pathway for Indians',
  '/study-abroad/germany/pr-after-study':       'Germany PR After Study 2026 — Graduation to Settlement Roadmap',
  '/study-abroad/germany/jobs-after-masters':   'Jobs After Masters in Germany 2026 — Salaries, Sectors & Blue Card Path',
  '/study-abroad/germany/student-accommodation':'Germany Student Accommodation 2026 — Dorms, WGs & Private Flats',
  '/study-abroad/germany/intakes':              'Germany University Intakes 2026 — Winter vs Summer Compared',
};

const CURATED_DESCRIPTIONS = {
  '/study-abroad/germany/aps-certificate':
    'APS Certificate for Indian students applying to German universities. Eligibility, ₹18,500 fee structure, 9-document checklist, 6-step process and 4–8 week timeline.',
  '/study-abroad/germany/blocked-account':
    'Germany Blocked Account guide — €11,904 funding rule, Fintiba vs Expatrio vs Coracle comparison, 6-step opening process, withdrawal rules, visa-rejection refund policy.',
  '/study-abroad/germany/cost-of-study':
    'Full cost of studying in Germany 2026 — tuition (€0–€1,500), blocked account €11,904, monthly living costs by city, insurance, one-time setup costs from India.',
  '/study-abroad/germany/scholarships':
    'Germany scholarships 2026 — DAAD, Erasmus+, Deutschlandstipendium, Heinrich Böll, Konrad-Adenauer & Bayer. Award sizes, eligibility, deadlines and tips for Indians.',
  '/study-abroad/germany/public-universities':
    'Germany public universities 2026 — tuition-free TU9 alliance, top research universities, 1,000+ English-taught Masters, GPA cutoffs and Uni-Assist application process.',
  '/study-abroad/germany/ausbildung':
    'Ausbildung in Germany 2026 — paid 3-year vocational training for Indians. 8 top trades, B1/B2 language rules, stipends €1,000–€1,400/mo, EU Blue Card pathway.',
  '/study-abroad/germany/pr-after-study':
    'Germany PR after study 2026 — EU Blue Card vs §18a vs §18b pathways, 21-month timeline, tax milestones, fast-track checklist for Indian STEM graduates.',
  '/study-abroad/germany/jobs-after-masters':
    'Jobs after Masters in Germany 2026 — 7 high-demand sectors with salaries, 18-month job-seeker visa rules, transition to EU Blue Card, recruitment tactics for Indians.',
  '/study-abroad/germany/student-accommodation':
    'Germany student accommodation 2026 — Studentenwerk vs WG vs private flats, costs by city, 12-month booking timeline, and 5 common housing scams to avoid.',
  '/study-abroad/germany/intakes':
    'Germany university intakes 2026 — Winter (October) vs Summer (April) comparison, application deadline calendars and a 9-item document checklist for Indian applicants.',
  '/study-abroad':
    'Zafco Study Abroad — MEA-approved overseas education consultants. End-to-end support for UK, Canada, Australia, Germany, Ireland & Dubai universities, student visas, scholarships and post-study work pathways.',
  '/study-abroad/germany':
    'Study in Germany with Zafco — admission support for TU9 & top public universities, free APS guidance, blocked account, student-visa filing and post-study Opportunity Card pathway.',
  '/study-abroad/uk':
    'Study in UK with Zafco — Russell Group university admissions, UK Student Visa (Tier-4) support, scholarship hunting and Graduate Route work-visa guidance.',
  '/study-abroad/canada':
    'Study in Canada with Zafco — SDS-stream admissions, PAL & DLI guidance, study-permit support, PGWP & Express Entry PR pathway for Indian students.',
  '/study-abroad/australia':
    'Study in Australia with Zafco — Group of Eight & top-rated university admissions, Subclass 500 student visa, GTE statement design and 491/189 PR pathway support.',
  '/study-abroad/ireland':
    'Study in Ireland with Zafco — Trinity, UCD and top university admissions, student visa support and 2-year Stamp 1G post-study work guidance.',
  '/study-abroad/dubai':
    'Study in Dubai with Zafco — KHDA-listed university admissions, UAE student visa, Green Visa pathway and DIFC career-track planning for Indian students.',
  '/study-abroad/cost-calculator':
    'Calculate your true cost of studying abroad — tuition, living, visa, insurance and travel. Live 2026 estimates for UK, Canada, Australia, Germany, Ireland and Dubai.',
  '/study-abroad/visa-eligibility':
    'Free 60-second student visa eligibility checker. Get a country-fit score across UK, Canada, Australia, Germany, Ireland and Dubai before you spend on applications.',
  '/study-abroad/book-counselling':
    'Book a free 1:1 counselling session with a Zafco Study Abroad expert. University shortlisting, visa-fit assessment and timeline planning — no charges, no obligation.',
  '/study-abroad/free-profile-evaluation':
    'Get a free academic + career profile evaluation from Zafco Study Abroad. Personalised university shortlist, visa-fit score and 2026 timeline — delivered within 48 hours.',
  '/study-abroad/privacy-policy':
    'Zafco Study Abroad privacy policy — how we collect, use, store and protect your personal data during overseas education advisory engagements.',
};

/* ────────────────────────────────────────────────────────────────────────
 *  SLUG HUMANISATION TABLES
 * ──────────────────────────────────────────────────────────────────────── */

// Codes that should stay all-uppercase when title-cased.
const UPPER_TOKENS = new Set([
  'uk', 'us', 'usa', 'uae', 'eu', 'ai', 'mba', 'bba', 'phd', 'msc',
  'bsc', 'beng', 'meng', 'llm', 'llb', 'cv', 'sop', 'gre', 'gmat',
  'ielts', 'pte', 'toefl', 'pr', 'pg', 'pgwp',
]);

// Country-slug → "Study in {Country}" prefix
const COUNTRY_LABEL = {
  germany:   'Germany',
  uk:        'UK',
  canada:    'Canada',
  australia: 'Australia',
  ireland:   'Ireland',
  dubai:     'Dubai',
};

// Sub-page slug → human topic label
const TOPIC_LABEL = {
  'student-visa':            'Student Visa Guide & Requirements',
  'best-universities':       'Top Universities & Programs',
  'cost-of-living':          'Cost of Living Guide',
  'work-rights':             'Work Rights & Part-Time Jobs',
  'golden-visa':             'Golden Visa Guide',
  'universities':            'Universities & Programs',
  'top-courses':             'Top Courses & Degree Programs',
  'about-zafco':             'About Zafco',
  'why-choose-zafco':        'Why Choose Zafco',
  'university-partnerships': 'University Partnerships',
  'events-webinars':         'Events & Webinars',
  'visa-success-stories':    'Visa Success Stories',
  'visa-comparison':         'Student Visa Comparison',
  'compare-tool':            'Compare Tool',
};

/* ────────────────────────────────────────────────────────────────────────
 *  HELPERS
 * ──────────────────────────────────────────────────────────────────────── */

const normalisePath = (p) => {
  if (!p) return '/study-abroad';
  const noTrail = p.replace(/\/+$/, '');
  return noTrail || '/study-abroad';
};

const titleCase = (slug) =>
  slug
    .split('-')
    .filter(Boolean)
    .map((w) => {
      const lc = w.toLowerCase();
      if (UPPER_TOKENS.has(lc)) return lc.toUpperCase();
      return lc.charAt(0).toUpperCase() + lc.slice(1);
    })
    .join(' ');

/**
 * Build a dynamic title from URL slug segments — used when no curated
 * entry exists. Handles 1-, 2-, and 3+-segment paths.
 *
 *   /study-abroad/dubai/about-zafco/  → "Dubai About Zafco"
 *   /study-abroad/uk/student-visa/    → "UK Student Visa Guide & Requirements"
 *   /study-abroad/random-new-page/    → "Random New Page"
 */
const buildAutoTitle = (path) => {
  const trimmed = path.replace(/^\/study-abroad\/?/, '');
  if (!trimmed) return 'Study Abroad Consultants & Overseas Education';
  const parts = trimmed.split('/').filter(Boolean);

  // Single-segment route — could be a country hub or a generic utility page.
  if (parts.length === 1) {
    if (COUNTRY_LABEL[parts[0]]) {
      return `Study in ${COUNTRY_LABEL[parts[0]]} — Top Universities & Admissions`;
    }
    return titleCase(parts[0]);
  }

  // Two-segment route — typically /{country}/{topic}/ or /dubai/{topic}/
  if (parts.length === 2) {
    const [country, topic] = parts;
    const countryLabel = COUNTRY_LABEL[country];
    const topicLabel = TOPIC_LABEL[topic] || titleCase(topic);
    if (countryLabel) return `${countryLabel} ${topicLabel}`;
    return `${titleCase(country)} ${topicLabel}`;
  }

  // Deeper — combine last two slugs (avoids ultra-long titles).
  const last = parts.slice(-2);
  const lastTopic = TOPIC_LABEL[last[1]] || titleCase(last[1]);
  return `${titleCase(last[0])} ${lastTopic}`;
};

/**
 * Build a dynamic description. Strategy: extract the country (if present)
 * and the topic, then template-fill a 130–160 char meta description.
 */
const buildAutoDescription = (path, title) => {
  const trimmed = path.replace(/^\/study-abroad\/?/, '');
  if (!trimmed) return SA_DEFAULT_DESC;
  const parts = trimmed.split('/').filter(Boolean);
  const country = parts.length > 0 && COUNTRY_LABEL[parts[0]]
    ? COUNTRY_LABEL[parts[0]]
    : null;

  if (country && parts.length >= 2) {
    return `${title} — expert guidance from Zafco Study Abroad. MEA-approved consultants for ${country} education, student visa filing, scholarships and post-study work pathways for Indian students.`;
  }
  if (country) {
    return `${title}. Zafco Study Abroad — MEA-approved consultants for ${country} university admissions, student visa, scholarships and 2026 application timelines.`;
  }
  return `${title}. Free expert guidance from Zafco Study Abroad — MEA-approved overseas education consultants. Free 1:1 counselling and personalised university shortlist for Indian students.`;
};

/* ────────────────────────────────────────────────────────────────────────
 *  PUBLIC HOOK
 * ──────────────────────────────────────────────────────────────────────── */

const ensureMeta = (selector, attrName, attrValue) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  return el;
};

const useStudyAbroadAutoMeta = () => {
  const location = useLocation();

  // Regular `useEffect` (not `useLayoutEffect`) — fires AFTER all child
  // effects (e.g. `usePageMeta(...)`) so we can read the title the child
  // actually set, then enforce the silo's `| Zafco Study Abroad` brand
  // suffix on it. Pages that did NOT set their own title still hold the
  // leaked recruitment fallback at this point — we detect that signature
  // and replace it with the auto-computed Study-Abroad title.
  useEffect(() => {
    const path = location.pathname || '/study-abroad';
    if (!path.startsWith('/study-abroad')) return;

    const normalised = normalisePath(path);
    const curatedTitle = CURATED_TITLES[normalised];
    const curatedDesc = CURATED_DESCRIPTIONS[normalised];

    // ─── TITLE ────────────────────────────────────────────────────────
    const currentTitle = document.title || '';
    let finalCoreTitle;
    if (RECRUITMENT_LEAK_TITLES.includes(currentTitle) || !currentTitle.trim()) {
      // Recruitment leak OR empty — use curated > auto-built from slugs.
      finalCoreTitle = curatedTitle || buildAutoTitle(normalised);
    } else {
      // Child set its own title. Strip any trailing `| Zafco …` brand
      // (HR, HRM, Study Abroad, plain Zafco) so we can re-append the
      // silo-correct suffix in one canonical form.
      finalCoreTitle = currentTitle.replace(TRAILING_BRAND_RE, '').trim();
    }
    const finalTitle = `${finalCoreTitle} ${SA_SUFFIX}`;
    if (currentTitle !== finalTitle) document.title = finalTitle;

    // ─── DESCRIPTION ─────────────────────────────────────────────────
    const metaDescEl = ensureMeta('meta[name="description"]', 'name', 'description');
    const currentDesc = metaDescEl.getAttribute('content') || '';
    const looksLikeLeak =
      currentDesc.startsWith(RECRUITMENT_LEAK_DESC_PREFIX) || !currentDesc.trim();
    const finalDesc = looksLikeLeak
      ? (curatedDesc || buildAutoDescription(normalised, finalCoreTitle))
      : currentDesc; // child-provided description — keep it.
    metaDescEl.setAttribute('content', finalDesc);

    // ─── OG / TWITTER MIRRORS ─────────────────────────────────────────
    const ogTitle = ensureMeta('meta[property="og:title"]', 'property', 'og:title');
    ogTitle.setAttribute('content', finalTitle);
    const ogDesc = ensureMeta('meta[property="og:description"]', 'property', 'og:description');
    ogDesc.setAttribute('content', finalDesc);
    const twTitle = ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title');
    twTitle.setAttribute('content', finalTitle);
    const twDesc = ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description');
    twDesc.setAttribute('content', finalDesc);
  }, [location.pathname]);
};

export default useStudyAbroadAutoMeta;
